import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import { Sizes } from '@/components/ui/types';

import {
  TableBodyProps,
  TableCellProps,
  TableContainerProps,
  TableFootProps,
  TableHeadProps,
  TableHeaderCellProps,
  TableProps,
  TableRowProps,
} from './types';

const sizeClasses: Record<Sizes, string> = {
  xs: 'table-xs',
  sm: 'table-sm',
  md: 'table-md',
  lg: 'table-lg',
  xl: 'table-xl',
};

const TableBase = forwardRef<HTMLTableElement, TableProps>(
  (
    {
      className,
      size = 'md',
      zebra = false,
      pinRows = false,
      pinCols = false,
      children,
      ...props
    },
    ref,
  ) => {
    const classes = twMerge(
      'table',
      sizeClasses[size],
      zebra && 'table-zebra',
      pinRows && 'table-pin-rows',
      pinCols && 'table-pin-cols',
      className,
    );
    return (
      <table ref={ref} className={classes} {...props}>
        {children}
      </table>
    );
  },
);

const Container = forwardRef<HTMLDivElement, TableContainerProps>(
  ({ className, children, ...props }, ref) => (
    <div ref={ref} className={twMerge('overflow-x-auto', className)} {...props}>
      {children}
    </div>
  ),
);

const Head = forwardRef<HTMLTableSectionElement, TableHeadProps>(
  ({ className, children, ...props }, ref) => (
    <thead ref={ref} className={twMerge(className)} {...props}>
      {children}
    </thead>
  ),
);

const Body = forwardRef<HTMLTableSectionElement, TableBodyProps>(
  ({ className, children, ...props }, ref) => (
    <tbody ref={ref} className={twMerge(className)} {...props}>
      {children}
    </tbody>
  ),
);

const Foot = forwardRef<HTMLTableSectionElement, TableFootProps>(
  ({ className, children, ...props }, ref) => (
    <tfoot ref={ref} className={twMerge(className)} {...props}>
      {children}
    </tfoot>
  ),
);

const Row = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, children, ...props }, ref) => (
    <tr ref={ref} className={twMerge(className)} {...props}>
      {children}
    </tr>
  ),
);

const HeaderCell = forwardRef<HTMLTableCellElement, TableHeaderCellProps>(
  ({ className, children, ...props }, ref) => (
    <th ref={ref} className={twMerge(className)} {...props}>
      {children}
    </th>
  ),
);

const Cell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, children, ...props }, ref) => (
    <td ref={ref} className={twMerge(className)} {...props}>
      {children}
    </td>
  ),
);

TableBase.displayName = 'Table';
Container.displayName = 'Table.Container';
Head.displayName = 'Table.Head';
Body.displayName = 'Table.Body';
Foot.displayName = 'Table.Foot';
Row.displayName = 'Table.Row';
HeaderCell.displayName = 'Table.HeaderCell';
Cell.displayName = 'Table.Cell';

export const Table = Object.assign(TableBase, {
  Container,
  Head,
  Body,
  Foot,
  Row,
  HeaderCell,
  Cell,
});
